import React, { useState } from 'react';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { exportAppChargesExcel } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const PERIODS = [
  { value: 'today', label: 'Sot' },
  { value: '7d', label: '7 ditët e fundit' },
  { value: 'this_month', label: 'Ky muaj' },
  { value: 'last_month', label: 'Muaji i kaluar' },
  { value: '90d', label: '3 muajt e fundit' },
  { value: 'this_year', label: 'Ky vit' },
  { value: 'last_year', label: 'Viti i kaluar' },
  { value: '365d', label: '1 vit (365 ditë)' },
  { value: 'custom', label: 'Nga data – deri me' },
  { value: 'all', label: 'E gjithë historia' },
];

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const filenameFromDisposition = (header, fallback) => {
  if (!header) return fallback;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      return encoded[1];
    }
  }
  const plain = header.match(/filename="?([^"]+)"?/i);
  return plain ? plain[1] : fallback;
};

const AppChargesExcelDialog = ({ open, onOpenChange, scope, targetId, targetName }) => {
  const [period, setPeriod] = useState('this_month');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [busyKind, setBusyKind] = useState('');

  const handleExport = async (kind) => {
    if (!targetId) return;
    if (period === 'custom' && (!fromDate || !toDate)) {
      toast.error('Zgjidhni datën e fillimit dhe të mbarimit');
      return;
    }
    if (period === 'custom' && toDate < fromDate) {
      toast.error('Data e mbarimit është para datës së fillimit');
      return;
    }

    setBusyKind(kind);
    try {
      const response = await exportAppChargesExcel({
        scope,
        id: targetId,
        kind,
        period,
        from_date: period === 'custom' ? fromDate : undefined,
        to_date: period === 'custom' ? toDate : undefined,
      });
      const fallback = `${targetName || 'albmove'}_${kind}.xlsx`;
      const filename = filenameFromDisposition(response.headers?.['content-disposition'], fallback);
      downloadBlob(response.data, filename);
      toast.success(kind === 'summary' ? 'Përmbledhja u shkarkua' : 'Lista e karikimeve u shkarkua');
    } catch (error) {
      let detail = 'Gabim gjatë eksportimit';
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          detail = parsed.detail || detail;
        } catch {
          detail = 'Gabim gjatë eksportimit';
        }
      } else if (data?.detail) {
        detail = data.detail;
      }
      toast.error(typeof detail === 'string' ? detail : 'Gabim gjatë eksportimit');
    } finally {
      setBusyKind('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white font-outfit flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
            Eksporto Excel
          </DialogTitle>
          <DialogDescription>
            {targetName ? `${targetName}. ` : ''}
            Vetëm karikimet që klienti i ka nisur vetë nga aplikacioni AlbMove.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Periudha</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="bg-zinc-800 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {PERIODS.map((item) => (
                  <SelectItem key={item.value} value={item.value} className="text-zinc-200">
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {period === 'custom' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="app-export-from">Nga data</Label>
                <Input
                  id="app-export-from"
                  type="date"
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                  className="bg-zinc-800 border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="app-export-to">Deri me</Label>
                <Input
                  id="app-export-to"
                  type="date"
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                  className="bg-zinc-800 border-white/10"
                />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-1">
            <Button
              type="button"
              onClick={() => handleExport('summary')}
              disabled={!!busyKind}
              className="justify-start"
            >
              {busyKind === 'summary' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Përmbledhje klientësh
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleExport('detail')}
              disabled={!!busyKind}
              className="justify-start"
            >
              {busyKind === 'detail' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Lista e karikimeve
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AppChargesExcelDialog;
